import React, { useState, useRef, useEffect } from 'react';
import { useSearchBox, useInstantSearch } from 'react-instantsearch-hooks-web';
import { MagnifyingGlassIcon as SearchIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { AnyARecord } from 'dns';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop } from 'helpers/utils/screensizes';
import NotFound from '../Algolia-search-Page/NotFound';

interface AlgoliaSearchBoxProps {
  onFocusChange: (focused: boolean) => void;
  setQueryString: (setsubmited: string) => void;
  setsubmited: (setsubmited: boolean) => void;
  isFocused: boolean;
  handleSuggestionClick: () => void;
  hitMatch: any;
  isSearchPage: boolean;
}

const products = [
  'Titleist SM10 Wedge',
  'Golf Midlayers',
  'New Balance',
  'Range Finder',
  'Titleist Onyx Collection',
  'BOSS',
  'Callaway Ai Smoke',
  'Wilson',
  'Tees',
  'Callaway Mavrik',
  'Taylor Made Qi10',
  'Umbrella',
  'Stromberg',
];
const AlgoliaSearchBox: React.FC<AlgoliaSearchBoxProps> = ({
  onFocusChange,
  setQueryString,
  setsubmited,
  isFocused,
  handleSuggestionClick,
  hitMatch,
  isSearchPage,
}) => {
  const { query, refine } = useSearchBox();
  const { status } = useInstantSearch();
  const [inputValue, setInputValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [pauseBeforeNext, setPauseBeforeNext] = useState(true);
  const [placeholder, setPlaceholder] = useState('Search American Golf...');
  const [isDesktop] = useMediaQuery(mediumDesktop);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const setQuery = (newQuery: string) => {
    setInputValue(newQuery);
    setQueryString(newQuery);
    refine(newQuery);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (hitMatch === null && /^\d+$/.test(inputValue as any) && inputValue !== '9988004001005') {
      return router.push(`/${inputValue}.html`);
    } else if (inputValue === '9988004001005' || inputValue.includes('voucher') || inputValue.includes('gift')) {
      router.push('/services/giftcards-evouchers.html');
    } else if (!inputValue) {
      return <></>;
    }
    setInputValue('');
    setsubmited(true);
    const trimmedValue = inputValue.trim();

    if (inputRef.current) {
      inputRef.current.blur();
    }

    if (!/^\d+$/.test(inputValue as any) && !inputValue.includes('voucher') && !inputValue.includes('gift')) {
      window.location.href = `/search/?q=${encodeURIComponent(trimmedValue)}`;
    }
  };

  useEffect(() => {
    if (isSearchPage) {
      setPlaceholder('');
    }
    if (!isDesktop || isFocused || pauseBeforeNext) {
      return;
    }
    if (isFocused) {
      setPlaceholder('');
    }

    const product = products[currentProductIndex];
    const interval = setInterval(() => {
      if (charIndex <= product.length) {
        setPlaceholder(`${product.substring(0, charIndex)}`);
        setCharIndex(charIndex + 1);
      } else {
        clearInterval(interval);
        setCharIndex(0);
        setCurrentProductIndex((currentProductIndex + 1) % products.length);
        setPauseBeforeNext(true);
      }
    }, 180);

    return () => clearInterval(interval);
  }, [isDesktop, isFocused, currentProductIndex, charIndex, pauseBeforeNext, isSearchPage]);

  useEffect(() => {
    if (pauseBeforeNext && !isSearchPage) {
      setPlaceholder('Search American Golf...');
      const timeout = setTimeout(() => {
        setPauseBeforeNext(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [pauseBeforeNext]);
  return (
    <div className="mx-auto mt-5 w-[310px] max-w-md">
      <form action="" role="search" noValidate onSubmit={handleSubmit} className="relative">
        <input
          ref={inputRef}
          autoComplete="on"
          autoCorrect="off"
          autoCapitalize="off"
          placeholder={isFocused ? '' : placeholder}
          spellCheck={true}
          maxLength={512}
          type="search"
          value={inputValue}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onFocus={() => {
            handleSuggestionClick();
          }}
          onBlur={() => onFocusChange(false)}
          className="w-full truncate rounded-lg border border-black bg-white px-4 py-10 text-sm text-gray-700 shadow-[0_4px_6px_rgba(169,169,169,0.5)] transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
        />
        <button
          type="submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 transform text-indigo-600 hover:text-indigo-700 focus:outline-none"
        >
          <SearchIcon className="mr-8 h-24 w-24 stroke-secondary-black" />
        </button>
      </form>
    </div>
  );
};

export default AlgoliaSearchBox;
